import type { SpriteTarget } from "./pty.js";

/**
 * The shell's half of the Sprite control plane (§4.6.5).
 *
 * Provisioning is ~77 seconds (§4.6.6), so this is a poll, not a call. The
 * shape is deliberate: `ensure()` returns immediately with whatever is true
 * now, and the caller decides how to show "still building" — a spinner that
 * blocks the workspace for over a minute would be a worse lie than saying so.
 *
 * The flake is sent from here because the server never learns Yjs (§4.2): the
 * environment lives in the document, so only a client can read it.
 */

export type SpriteStatus =
  | { state: "none" }
  | { state: "provisioning"; name: string }
  | { state: "failed"; name: string }
  | { state: "ready"; name: string; target: SpriteTarget };

export type ControlPlane = {
  get(): Promise<SpriteStatus>;
  ensure(flake: string): Promise<SpriteStatus>;
};

export function controlPlane(workspaceId: string, base = ""): ControlPlane {
  const url = `${base}/api/workspaces/${encodeURIComponent(workspaceId)}/sprite`;

  const read = async (response: Response): Promise<SpriteStatus> => {
    if (response.status === 404) return { state: "none" };
    const body = (await response.json()) as Record<string, unknown>;
    const name = typeof body["name"] === "string" ? body["name"] : "";
    switch (body["status"]) {
      case "ready": {
        const origin = body["origin"];
        const token = body["token"];
        if (typeof origin !== "string" || origin === "" || typeof token !== "string") {
          // Ready without somewhere to reach it is not ready.
          return { state: "failed", name };
        }
        return { state: "ready", name, target: { origin, token } };
      }
      case "provisioning":
        return { state: "provisioning", name };
      default:
        return { state: "failed", name };
    }
  };

  return {
    async get(): Promise<SpriteStatus> {
      const response = await fetch(url, { headers: { accept: "application/json" } });
      return read(response);
    },
    async ensure(flake: string): Promise<SpriteStatus> {
      const response = await fetch(url, {
        method: "POST",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ flake }),
      });
      return read(response);
    },
  };
}
